/* línkè · 背景色中枢（2026-07-04，仿 accent.js） ===================
   用户选【一个底色主色】→ 按【当前主题各自的阶梯配方】推导出整套背景令牌
   （--page-bg / --void / --deep / --surface / --surface-2 / --line
     + --void-rgb / --deep-rgb + --sky-grad），内联挂到 <html>。
   「跟随主题」= null = 摘掉，主题原底色复活。
   存 localStorage、每台设备各自记（和主题/强调色同待遇，不进云同步）。

   存的是【token】而非死颜色：
     '@sand' 等 = 预设「色调身份」→ 切到哪个主题就按该主题的明暗【现算】具体色，
                  所以同一个暖沙在深主题=深暖褐、浅主题=奶油色，永远明度匹配主题；
     '#rrggbb'  = 自定义具体色（你为当前主题亲手挑的，保持字面、不跨主题变形）。

   为什么每主题一套阶梯配方：三主题的底色阶梯方向/幅度都不同（夜空是「主色大幅
   压黑当 void、微提白当 surface」，晨光/绯霞几乎贴着白、幅度很小）。配方是拿
   各主题【原生底色相对 page-bg 的偏移】反推的 → 不选色时能还原原样。
   文字对比【不自动处理】：预设已按主题明暗落位；自定义时你在深主题挑深、浅主题挑浅即可。
   ================================================================ */
import { useState, useEffect } from 'react';

export const STORAGE_BG = 'lingke.bg';

/* 各主题底色（= tokens.css 里每主题的 --page-bg，也是自定义取色器的默认起点）。 */
export const THEME_PAGE_BG = {
  dark: '#15102a', light: '#f7ede3', rose: '#fbeee9', snow: '#f1f5fa',
};

/* 预设「色调身份」：只定色相 hue + 饱和 sat，明度交给主题。挑的都是做背景公认
   耐看的治愈/莫兰迪系。id 存成 '@id'。跟随主题(null)在 UI 里单独渲染。 */
export const BG_PRESETS = [
  { id: 'sand', name: '暖沙', hue: 35, sat: 0.45 },
  { id: 'greige', name: '雾灰', hue: 30, sat: 0.1 },
  { id: 'sage', name: '鼠尾草', hue: 150, sat: 0.3 },
  { id: 'dusk', name: '黄昏蓝', hue: 220, sat: 0.42 },
  { id: 'lavender', name: '薰衣草', hue: 265, sat: 0.4 },
  { id: 'blush', name: '藕荷', hue: 340, sat: 0.42 },
];

/* 每主题的「背景明度 L + 饱和柔化系数 k」：预设色 = HSL(hue, sat×k, L)。
   L 取各主题 page-bg 的明度（预设与主题一样深/浅），浅主题柔化饱和让色更淡雅。 */
const THEME_BG = {
  dark: { L: 0.12, k: 1.0 },
  light: { L: 0.929, k: 0.5 }, // 日落：底色本身偏暖偏深一点，柔化也更狠
  rose: { L: 0.95, k: 0.62 },
  snow: { L: 0.963, k: 0.5 }, // 初雪：几乎贴着白
};

/* 阶梯配方：token → [方向, 比例]。方向 'k'=向黑混、'w'=向白混，比例 0~1。
   数值由各主题原生令牌相对其 page-bg 反推（base=原 page-bg 时可复原原色）。 */
const RECIPES = {
  dark: {
    void: ['k', 0.5], deep: ['k', 0.29],
    surface: ['w', 0.02], surface2: ['w', 0.05], line: ['w', 0.1],
  },
  light: {
    void: ['k', 0.04], deep: ['k', 0.076],
    surface: ['w', 0.59], surface2: ['k', 0.02], line: ['k', 0.145],
  },
  rose: {
    void: ['k', 0.048], deep: ['k', 0.108],
    surface: ['w', 0.32], surface2: ['k', 0.039], line: ['k', 0.109],
  },
  snow: {
    void: ['k', 0.025], deep: ['k', 0.041],
    surface: ['w', 0.838], surface2: ['k', 0.01], line: ['k', 0.098],
  },
};

const _listeners = new Set();

function _validHex(v) {
  return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
}
function _validToken(v) {
  return _validHex(v) || (typeof v === 'string' && v[0] === '@' && BG_PRESETS.some((p) => p.id === v.slice(1)));
}

function hexToRgb(hex) {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}
function rgbToHex(r, g, b) {
  const h = (n) => Math.min(255, Math.max(0, Math.round(n))).toString(16).padStart(2, '0');
  return '#' + h(r) + h(g) + h(b);
}
/* HSL(0-360,0-1,0-1) → #hex */
function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360 / 360;
  const f = (n) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return rgbToHex(f(0) * 255, f(8) * 255, f(4) * 255);
}
/* 把 base 向黑(0)或白(255)混 a 比例 */
function mix(rgb, dir, a) {
  const t = dir === 'w' ? 255 : 0;
  return rgb.map((c) => c * (1 - a) + t * a);
}

/* 读 <html data-theme>（真正在渲染的主题），不读 localStorage：切主题时 applyTheme 先改
   data-theme、最后才存 localStorage，读 localStorage 会拿到【旧主题】→ 深色切绯霞时
   预设字色还按深色算成浅字（2026-10-08 铃逮到）。 */
function currentTheme() {
  try {
    const v = document.documentElement.getAttribute('data-theme');
    if (v === 'light' || v === 'rose' || v === 'snow') return v;
  } catch {}
  return 'dark';
}

/* 预设在【指定主题】下的具体底色 hex（UI 画色块 + 应用时都用它）。 */
export function presetColorFor(presetId, themeId) {
  const p = BG_PRESETS.find((x) => x.id === presetId);
  if (!p) return null;
  const th = THEME_BG[themeId] || THEME_BG.dark;
  return hslToHex(p.hue, p.sat * th.k, th.L);
}

/* token → 当前主题下的具体底色 hex（预设现算 / 自定义原样），非法/null → null。 */
function resolveBase(token) {
  if (_validHex(token)) return token;
  if (typeof token === 'string' && token[0] === '@') return presetColorFor(token.slice(1), currentTheme());
  return null;
}

/* 返回存的 token（'@id' | '#hex' | null）。 */
export function getBg() {
  try {
    const v = localStorage.getItem(STORAGE_BG);
    return _validToken(v) ? v : null;
  } catch {
    return null;
  }
}

/* 自定义取色器的默认起点（当前主题底色，明度天然对得上）。 */
export function themePageBg() {
  return THEME_PAGE_BG[currentTheme()] || THEME_PAGE_BG.dark;
}

export function applyBg(token) {
  const root = document.documentElement;
  const hex = resolveBase(token);
  if (!hex) {
    root.removeAttribute('data-bg');
    ['--page-bg', '--void', '--deep', '--surface', '--surface-2', '--line',
     '--void-rgb', '--deep-rgb', '--sky-grad'].forEach((p) => root.style.removeProperty(p));
    return;
  }
  const base = hexToRgb(hex);
  const r = RECIPES[currentTheme()] || RECIPES.dark;
  const voidRgb = mix(base, ...r.void);
  const deepRgb = mix(base, ...r.deep);
  const set = (p, v) => root.style.setProperty(p, v);
  set('--page-bg', hex);
  set('--void', rgbToHex(...voidRgb));
  set('--deep', rgbToHex(...deepRgb));
  set('--surface', rgbToHex(...mix(base, ...r.surface)));
  set('--surface-2', rgbToHex(...mix(base, ...r.surface2)));
  set('--line', rgbToHex(...mix(base, ...r.line)));
  // rgba() 半透明用的原始分量（tabbar/输入框/浮标跟随）
  const trip = (a) => a.map((c) => Math.round(c)).join(', ');
  set('--void-rgb', trip(voidRgb));
  set('--deep-rgb', trip(deepRgb));
  // 整屏渐变：v1 用简洁的 base→deep→void 竖直渐变（原主题那两层极淡辉光暂不带，
  // 星星仍由 .sky 单独浮）。三主题通用，不会在浅底上突然压出深带。
  set('--sky-grad',
    `linear-gradient(180deg, ${hex} 0%, ${rgbToHex(...deepRgb)} 55%, ${rgbToHex(...voidRgb)} 100%)`);
  root.setAttribute('data-bg', '');
}

export function setBg(token) {
  const v = _validToken(token) ? token : null;
  try {
    if (v) localStorage.setItem(STORAGE_BG, v);
    else localStorage.removeItem(STORAGE_BG);
  } catch {}
  applyBg(v);
  _listeners.forEach((fn) => {
    try {
      fn(v);
    } catch {}
  });
}

/* 启动时（main.jsx，首帧前）恢复上次选色，避免刷新闪回主题原底色。 */
export function applyStoredBg() {
  applyBg(getBg());
}

/* React hook：[bgToken, setBg]。null = 跟随主题。 */
export function useBg() {
  const [bg, set] = useState(() => getBg());
  useEffect(() => {
    _listeners.add(set);
    return () => _listeners.delete(set);
  }, []);
  return [bg, setBg];
}
